import { captureException } from '@sentry/react';
import React from 'react';

export type FeatureErrorFallback = React.ReactNode | ((reset: () => void) => React.ReactNode);

export interface FeatureErrorBoundaryProps {
  feature: string;
  children: React.ReactNode;
  fallback: FeatureErrorFallback;
  onError?: (error: Error, info: React.ErrorInfo) => void;
  resetKeys?: readonly unknown[];
}

interface FeatureErrorBoundaryState {
  hasError: boolean;
  resetKeys: readonly unknown[];
}

const sameKeys = (a: readonly unknown[], b: readonly unknown[]): boolean =>
  a.length === b.length && a.every((value, index) => Object.is(value, b[index]));

export class FeatureErrorBoundary extends React.Component<FeatureErrorBoundaryProps, FeatureErrorBoundaryState> {
  constructor(props: FeatureErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, resetKeys: props.resetKeys ?? [] };
  }

  static getDerivedStateFromError(): Partial<FeatureErrorBoundaryState> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(
    props: FeatureErrorBoundaryProps,
    state: FeatureErrorBoundaryState,
  ): Partial<FeatureErrorBoundaryState> | null {
    const next = props.resetKeys ?? [];
    if (sameKeys(next, state.resetKeys)) return null;
    return { hasError: false, resetKeys: next };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    captureException(error, { tags: { feature: this.props.feature } });
    this.props.onError?.(error, info);
  }

  private reset = (): void => this.setState({ hasError: false });

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;
    const { fallback } = this.props;
    if (typeof fallback === 'function') return fallback(this.reset);
    return fallback ?? null;
  }
}
